import { utils, writeFileXLSX, read } from "xlsx";
import { setSnackbarDetails } from "../redux/AuthReducer/AuthSlice";
import { apiClient } from "./apiClient";
import { OptionType } from "../components/custom-select/Select";

type columns = {
  header: string;
  accessor: string;
};

export const formatDateOnly = (date: Date | string | null) => {
  if (!date) return "";

  const parsedDate = typeof date === "string" ? new Date(date) : date;
  if (isNaN(parsedDate.getTime())) return "";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`; // ✅ LOCAL DATE (no timezone shift)
};

export const formatTime = (
  date: Date | string | null,
  formatHourStatus: boolean,
) => {
  if (!date) return "";
  const parsedDate =
    typeof date === "string" ? new Date(`1970-01-01 ${date}`) : date;
  if (isNaN(parsedDate.getTime())) return "";
  return parsedDate.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: formatHourStatus,
  });
};

export const formatDate = (dateString: string | null) => {
  if (!dateString) return null;

  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

export const parseStringTimeToDate = (timeString: string | null) => {
  if (!timeString) return null;

  // Match "hh:mm AM/PM" or "hh:mm"
  const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const meridian = match[3]?.toUpperCase();

  if (meridian === "PM" && hours < 12) hours += 12;
  if (meridian === "AM" && hours === 12) hours = 0;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0); // seconds & ms = 0

  return date;
};

export const exportToExcel = (
  headers: string[],
  data: any[],
  pageName: string,
) => {
  // Create worksheet with controlled header order
  const worksheet = utils.json_to_sheet(data, {
    header: headers,
    skipHeader: false, // automatically creates header row
  });

  // Create workbook
  const workbook = utils.book_new();

  // Ensure sheet name is within 31 characters
  const safeSheetName = pageName.substring(0, 31);
  utils.book_append_sheet(workbook, worksheet, safeSheetName);

  // Download file
  writeFileXLSX(workbook, pageName);
};

export const downloadXMLTemplateFormat = (filename: string) => {
  const link = document.createElement("a");
  link.href = `/${filename}.xlsx`; // exact name
  link.download = `${filename}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type DebouncedFunction<T extends (...args: any[]) => void> = {
  (...args: Parameters<T>): void;
  cancel: () => void;
};

export const searchDebounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): DebouncedFunction<T> => {
  let timeout: NodeJS.Timeout;

  const debounced = ((...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  }) as DebouncedFunction<T>;

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return debounced;
};

export function handleValues(details: any) {
  const hasValue = Object.values(details || {}).every((value) => {
    if (typeof value === "string") {
      return value.trim().length > 0; // non-empty string
    } else if (Array.isArray(value)) {
      return value.length > 0; // non-empty array
    } else if (typeof value === "number") {
      return true; // numbers count as having a value
    } else if (typeof value === "boolean") {
      return value; // true counts as a value
    }
    return false; // null, undefined, objects => no value
  });
  return hasValue;
}

export const handleExportApiCall = async (
  url: string,
  dispatcher: any,
  name: string,
) => {
  try {
    let res = await apiClient?.get<any>(url);
    return res?.data?.data;
  } catch (error: any) {
    dispatcher(
      setSnackbarDetails({
        type: "error",
        message:
          error?.response?.data?.data?.message ??
          `Error In Fetching the ${name}`,
      }),
    );
  }
};

export const handleExportTableData = async (data: any, columns: columns[]) => {
  try {
    let exportData = data?.map((item: any) => {
      const row: any = {};

      columns?.forEach((col: any) => {
        let value = item[col.accessor];

        // Handle arrays
        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        //  Handle objects (avoid [object Object])
        else if (typeof value === "object" && value !== null) {
          value = JSON.stringify(value);
        }

        // Handle null/undefined
        row[col.header] = value ?? "";
      });

      return row;
    });

    return exportData || [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

export function getAnnualYearData() {
  const startYear = 2020;
  const currentYear = new Date().getFullYear();
  const years: OptionType[] = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => {
      const year = currentYear - i;

      let data = {
        id: String(i + 1),
        displayName: String(year),
      };
      return data;
    },
  );
  return years;
}
